import { useCallback, useEffect, useState } from 'react';
import ReactLoading from 'react-loading';
import { useSearchParams } from 'react-router-dom';
import EmptyPage from '../../../empty-page/emptyPage';
import CommonTable from '../../../reusable-ui-components/commonTable/CommonTable';
import Pagination from '../../../pagination/Pagination';
import Dialog from '../../../dialogModal/dialogModal';
import { orgStayPayoutFun, orgStayPayoutsFun } from '../../../../api/orgCampAPI';
import errorHandle from '../../../../utils/errorHandle';
import { day } from '../../orgStay/stayColumns';
import styles from '../../orgStay/orgStay.module.css';

/*
 * Organizer > Camp > Payouts: what we've paid you, what's due next, and the
 * bookings in each payout (click a row).
 */
const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;

const columns = [
    { label: 'Paid on', accessor: 'payoutDate', render: day },
    { label: 'Bookings', accessor: 'bookings' },
    { label: 'Amount', accessor: 'totalAmount', render: (val) => <strong>{inr(val)}</strong> },
    { label: 'Reference', accessor: 'reference', render: (val) => val || '—' },
    {
        label: '',
        accessor: '_id',
        render: () => <span className={styles.link}>View bookings</span>
    }
];

const detailColumns = [
    {
        label: 'Stay',
        accessor: 'contentId',
        render: (val, row) => (
            <div>
                <strong>{val?.title || 'N/A'}</strong>
                <div className={styles.muted}>{row.rooms}</div>
            </div>
        )
    },
    { label: 'Guest', accessor: 'guestName' },
    { label: 'Check-in', accessor: 'startDate', render: day },
    { label: 'Amount', accessor: 'payoutDetails', render: (val) => inr(val?.totalAmount) },
    { label: 'Commission', accessor: 'payoutDetails', render: (val) => inr(val?.commission) },
    { label: 'Paid', accessor: 'payoutDetails', render: (val) => <strong>{inr(val?.amount)}</strong> }
];

const CampPayouts = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const pageNumber = Number(searchParams.get('page')) || 1;
    const limit = 10;
    const offSet = (pageNumber - 1) * limit;
    const [data, setData] = useState({ data: [], totalCount: 0, paidTotal: 0, pendingTotal: 0 });
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(null); // payout with its bookings

    const updateURL = useCallback(
        (params) => setSearchParams({ ...Object.fromEntries([...searchParams]), ...params }),
        [searchParams, setSearchParams]
    );

    useEffect(() => {
        let gone = false;
        setLoading(true);
        orgStayPayoutsFun({ limit, offset: offSet })
            .then((res) => !gone && setData(res?.data || {}))
            .catch((error) => !gone && errorHandle(error))
            .finally(() => !gone && setLoading(false));
        return () => {
            gone = true;
        };
    }, [offSet]);

    // the table passes the clicked row's id
    const openPayout = async (id) => {
        const payoutId = typeof id === 'object' ? id?._id : id;
        if (!payoutId) return;
        setLoading(true);
        try {
            const res = await orgStayPayoutFun(payoutId);
            setOpen(res?.data || null);
        } catch (error) {
            errorHandle(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className='dashboard'>
            {loading && (
                <div className='loadingBackground'>
                    <ReactLoading type='spin' color='#006aff' className='loadingSpin' />
                </div>
            )}
            <div className='kpi-container'>
                <div className='kpi-card kpi-card-1'>
                    <h1>{inr(data.paidTotal)}</h1>
                    <h4>Paid to you so far</h4>
                </div>
                <div className='kpi-card kpi-card-2'>
                    <h1>{inr(data.pendingTotal)}</h1>
                    <h4>Due in the next payout</h4>
                </div>
            </div>
            {(data.data || []).length === 0 && !loading ? (
                <EmptyPage />
            ) : (
                <>
                    <CommonTable data={data.data || []} columns={columns} setRowClickId={openPayout} />
                    <Pagination
                        paginationClick={(n) => Math.ceil((data.totalCount || 0) / limit) > 1 && updateURL({ page: n })}
                        numberOfRows={data.totalCount || 0}
                        offSet={offSet}
                        pageNumber={pageNumber}
                        limit={limit}
                    />
                </>
            )}
            {open && (
                <Dialog
                    openModal={Boolean(open)}
                    heading={`Payout of ${inr(open.totalAmount)} · ${day(open.payoutDate)}`}
                >
                    <div className={styles.payoutBox}>
                        <CommonTable data={open.campIds || []} columns={detailColumns} setRowClickId={() => {}} />
                        <div className={styles.right}>
                            <button type='button' className={styles.ghostBtn} onClick={() => setOpen(null)}>
                                Close
                            </button>
                        </div>
                    </div>
                </Dialog>
            )}
        </div>
    );
};

export default CampPayouts;
